/**
 * AutismInformantSetupForm.js
 *
 * Initial informant setup for Adult Autism patients on the modular dashboard.
 *
 * Unlike the ADHD InformantSetupForm (adulthood + childhood life-stage wizard),
 * autism patients nominate a single informant during initial setup who answers
 * all 7 developmental history questionnaires. A second informant can optionally
 * be added later via the Manage Informants modal.
 *
 * Two-step flow:
 *   Step 1 (form) — collect informant details or record a skip reason
 *   Step 2 (completion) — summary/congratulations screen with Finish button
 *
 * On completion (submit or skip), sets hasCompletedInformantSetupForm on the
 * patient header document so the dashboard unlocks the patient's own forms.
 */
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { httpsCallable } from 'firebase/functions';
import { doc, updateDoc } from 'firebase/firestore';
import { CheckCircleIcon, XIcon, XCircleIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { functions, db, auth } from '../../../../firebase';
import { ModularPatientSelfDataContext } from '../../../../context/ModularPatientSelfDataContext';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { getPatientFacingTerminology } from '../../../../utils/terminology';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { validateEmail as sharedValidateEmail, validateName } from '../../../../utils/informantValidation';
import ButtonSpinner from '../../../../components/informantManagement/ButtonSpinner';
import WarningPanel from '../../../../components/informantManagement/WarningPanel';


// ─── Error Mapping ───────────────────────────────────────────────────────────
// Maps common Cloud Function error messages to user-friendly equivalents.

const ERROR_MESSAGE_MAP = {
  'The email address is already in use by another account.': 'This email address is already registered. Please use a different email.',
  'auth/email-already-exists': 'This email address is already registered. Please use a different email.',
  'auth/invalid-email': 'Please enter a valid email address.',
};

function getErrorMessage(error) {
  const raw = error?.message || 'Failed to set up informant. Please try again.';
  return ERROR_MESSAGE_MAP[raw] || raw;
}


// ─── Skip Form Sub-component ─────────────────────────────────────────────────
// Gray card layout with icon badge matching ADHD's skip UI pattern.

function SkipForm({ skipReason, onSkipReasonChange, onGoBack, terminology, hasError, errorMessage }) {
  const textareaRef = useRef(null);

  // Auto-resize textarea as content grows (matches ADHD skip form behaviour)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [skipReason]);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-3">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
          <XCircleIcon className="w-5 h-5 text-gray-500" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-800 mb-1">
            I can't provide a {terminology.lowerSingular}
          </h4>
          <p className="text-sm text-gray-600">
            Please tell us why you are not adding a {terminology.lowerSingular} at this time.
          </p>
        </div>
      </div>

      <textarea
        ref={textareaRef}
        value={skipReason}
        onChange={(e) => onSkipReasonChange(e.target.value)}
        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 min-h-[80px] resize-none"
        rows={3}
        placeholder="Please explain why you cannot add an informant"
        aria-label="Reason for not adding an informant"
      />

      {hasError && <WarningPanel variant="error">{errorMessage}</WarningPanel>}

      <button
        type="button"
        onClick={onGoBack}
        className="text-sm text-violet-600 hover:text-violet-800 font-medium"
      >
        &larr; Go back to add a {terminology.lowerSingular}
      </button>
    </div>
  );
}


// ─── Informant Details Form Sub-component ────────────────────────────────────
// Labeled form fields with inline validation styling.

function InformantDetailsForm({ informant, onChange, onToggleSkip, terminology }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="informant-first-name" className="block text-xs font-medium text-gray-700 mb-1">
            First name
          </label>
          <input
            id="informant-first-name"
            type="text"
            placeholder="First name"
            value={informant.firstName}
            onChange={(e) => onChange('firstName', sanitizeNameInput(e.target.value))}
            className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
              informant.hasError && !informant.firstName.trim()
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300'
            }`}
            aria-invalid={informant.hasError && !informant.firstName.trim()}
          />
        </div>
        <div>
          <label htmlFor="informant-last-name" className="block text-xs font-medium text-gray-700 mb-1">
            Last name
          </label>
          <input
            id="informant-last-name"
            type="text"
            placeholder="Last name"
            value={informant.lastName}
            onChange={(e) => onChange('lastName', sanitizeNameInput(e.target.value))}
            className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
              informant.hasError && !informant.lastName.trim()
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300'
            }`}
            aria-invalid={informant.hasError && !informant.lastName.trim()}
          />
        </div>
      </div>

      <div>
        <label htmlFor="informant-email" className="block text-xs font-medium text-gray-700 mb-1">
          Email address
        </label>
        <input
          id="informant-email"
          type="email"
          placeholder="Email address"
          value={informant.email}
          onChange={(e) => onChange('email', sanitizeEmailInput(e.target.value))}
          className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
            informant.hasError && informant.errorMessage?.toLowerCase().includes('email')
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300'
          }`}
          aria-invalid={informant.hasError && informant.errorMessage?.toLowerCase().includes('email')}
          aria-describedby={informant.hasError ? 'informant-error' : undefined}
        />
      </div>

      <div>
        <label htmlFor="informant-relationship" className="block text-xs font-medium text-gray-700 mb-1">
          Relationship to you
        </label>
        <input
          id="informant-relationship"
          type="text"
          placeholder="e.g. Mother, Partner, Sibling"
          value={informant.relationship}
          onChange={(e) => onChange('relationship', sanitizeNameInput(e.target.value))}
          className={`w-full border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 ${
            informant.hasError && !informant.relationship.trim() && informant.errorMessage?.toLowerCase().includes('relationship')
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300'
          }`}
          aria-invalid={informant.hasError && informant.errorMessage?.toLowerCase().includes('relationship')}
        />
      </div>

      {informant.hasError && (
        <WarningPanel variant="error" id="informant-error">{informant.errorMessage}</WarningPanel>
      )}

      <button
        type="button"
        onClick={onToggleSkip}
        className="text-xs text-violet-600 hover:text-violet-800"
      >
        Unable to provide these details?
      </button>
    </div>
  );
}


// ─── Completion Screen Sub-component ─────────────────────────────────────────
// Green success summary shown after informant submit or skip, with Finish button.

function CompletionScreen({ informant, wasSkipped, terminology, onFinish }) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="flex justify-center mb-3">
          <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center" aria-hidden="true">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          {wasSkipped
            ? `You can add a ${terminology.lowerSingular} later from your home page.`
            : `Your ${terminology.lowerSingular} has been set up successfully!`
          }
        </p>
      </div>

      {/* Summary card */}
      {!wasSkipped ? (
        <div className="bg-green-50 border border-green-200 rounded-md p-4" role="status">
          <div className="flex items-start space-x-3">
            <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-green-800 mb-2">Successfully Added</h4>
              <div className="space-y-1 text-sm text-green-800">
                <p><span className="font-medium">Name:</span> {informant.firstName} {informant.lastName}</p>
                <p className="break-all"><span className="font-medium">Email:</span> {informant.email}</p>
                <p><span className="font-medium">Relationship:</span> {informant.relationship}</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
          <div className="flex items-start space-x-3">
            <XCircleIcon className="w-6 h-6 text-gray-400 flex-shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-700">Skipped for now</h4>
              <p className="text-sm text-gray-600 mt-1">
                You can add a {terminology.lowerSingular} at any time from your home page.
              </p>
            </div>
          </div>
        </div>
      )}

      <p className="text-center text-sm text-gray-700">
        Your questionnaires are now available!
      </p>
      <p className="text-center text-xs text-gray-500">
        You can manage your {terminology.lowerSingular} from your home page at any time.
      </p>

      <button
        type="button"
        onClick={onFinish}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
      >
        Finish
        <ChevronRightIcon className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}


// ─── Main Component ──────────────────────────────────────────────────────────

const EMPTY_INFORMANT = {
  firstName: '',
  lastName: '',
  email: '',
  relationship: '',
  skipReason: '',
  isSubmitting: false,
  isSuccess: false,
  hasError: false,
  errorMessage: '',
};

export default function AutismInformantSetupForm({ isOpen, onClose }) {
  const { header, fetchPatientData } = useContext(ModularPatientSelfDataContext);
  const { userData } = useContext(GlobalStateContext);
  const t = getPatientFacingTerminology(userData?.organisation || header?.organisation);

  const [informant, setInformant] = useState({ ...EMPTY_INFORMANT });
  const [showSkip, setShowSkip] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  // Two-step flow: 'form' (step 1) or 'completion' (step 2)
  const [currentStep, setCurrentStep] = useState('form');
  const [wasSkipped, setWasSkipped] = useState(false);

  const mountedRef = useRef(true);
  const actionLockRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Reset form state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setInformant({ ...EMPTY_INFORMANT });
      setShowSkip(false);
      setSubmitError('');
      setCurrentStep('form');
      setWasSkipped(false);
      fetchPatientData?.();
    }
  }, [isOpen, fetchPatientData]);

  /**
   * Validate all required fields on the informant form.
   * Returns a user-facing error string or null if valid.
   */
  const validateSlot = useCallback((inf) => {
    const firstErr = validateName(inf.firstName, 'First name');
    if (firstErr) return firstErr;
    const lastErr = validateName(inf.lastName, 'Last name');
    if (lastErr) return lastErr;
    const emailErr = sharedValidateEmail(inf.email);
    if (emailErr) return emailErr;
    if (!inf.relationship?.trim()) return 'Relationship is required';
    return null;
  }, []);

  /**
   * Call the setupNewInformant Cloud Function with autism-specific payload.
   * Returns true on success, false on failure.
   */
  const submitInformant = async () => {
    if (actionLockRef.current) return false;
    actionLockRef.current = true;

    const validationError = validateSlot(informant);
    if (validationError) {
      setInformant((prev) => ({ ...prev, hasError: true, errorMessage: validationError }));
      actionLockRef.current = false;
      return false;
    }

    setInformant((prev) => ({ ...prev, isSubmitting: true, hasError: false, errorMessage: '' }));

    try {
      const setupFn = httpsCallable(functions, 'setupNewInformant');
      await setupFn({
        informantFirstName: informant.firstName.trim(),
        informantLastName: informant.lastName.trim(),
        informantEmail: informant.email.trim().toLowerCase(),
        informantRelationshipToPatient: informant.relationship.trim(),
        organisation: userData?.organisation || header?.organisation || '',
        assessmentModule: 'adultAutism',
        isPartOfInitialSetup: true,
      });

      if (mountedRef.current) {
        setInformant((prev) => ({ ...prev, isSubmitting: false, isSuccess: true }));
        fetchPatientData?.();
      }
      return true;
    } catch (error) {
      if (mountedRef.current) {
        setInformant((prev) => ({
          ...prev,
          isSubmitting: false,
          hasError: true,
          errorMessage: getErrorMessage(error),
        }));
      }
      return false;
    } finally {
      actionLockRef.current = false;
    }
  };

  /**
   * Write hasCompletedInformantSetupForm to the patient header document,
   * optionally recording the skip reason if the patient chose not to add
   * an informant at this time. On success, transitions to the completion screen.
   */
  const markSetupComplete = async (skipReasons = {}) => {
    const patientUid = auth.currentUser?.uid;
    if (!patientUid || isCompleting) return;
    setIsCompleting(true);
    try {
      const patientRef = doc(db, 'patients', patientUid);
      await updateDoc(patientRef, {
        hasCompletedInformantSetupForm: true,
        informantSetupCompletedAt: new Date().toISOString(),
        ...(Object.keys(skipReasons).length > 0 ? { informantSkipReasons: skipReasons } : {}),
      });

      // Notify the provider team that informant setup is complete (non-blocking).
      // Mirrors ADHD's InformantSetupForm behaviour. The backend deduplicates via
      // informantSetupEmailSent so this is safe to fire even if the trigger races.
      try {
        const sendSetupEmail = httpsCallable(functions, 'sendInformantSetupEmail');
        await sendSetupEmail({
          informants: informant.isSuccess ? {
            informant1: {
              firstName: informant.firstName.trim(),
              lastName: informant.lastName.trim(),
              email: informant.email.trim().toLowerCase(),
              relationship: informant.relationship.trim(),
            }
          } : {},
          skipReasons,
        });
      } catch (emailErr) {
        console.error('Informant setup notification failed (non-blocking):', emailErr);
      }

      await fetchPatientData?.();

      // Transition to completion screen instead of closing immediately
      if (mountedRef.current) {
        setWasSkipped(Object.keys(skipReasons).length > 0);
        setCurrentStep('completion');
      }
    } catch (err) {
      console.error('Failed to mark informant setup complete:', err);
      setSubmitError('Could not save your progress. Please try again.');
    } finally {
      if (mountedRef.current) setIsCompleting(false);
    }
  };

  /**
   * Primary action handler — either submits the informant or processes
   * the skip, then marks setup as complete (which transitions to completion screen).
   */
  const handleSubmit = async () => {
    if (showSkip) {
      if (!informant.skipReason.trim()) {
        setInformant((prev) => ({
          ...prev,
          hasError: true,
          errorMessage: 'Please provide a reason for not adding an informant.',
        }));
        return;
      }
      const skipReasons = { informant1: informant.skipReason.trim() };
      await markSetupComplete(skipReasons);
      return;
    }

    if (informant.isSuccess) {
      await markSetupComplete();
      return;
    }

    const ok = await submitInformant();
    if (ok) {
      await markSetupComplete();
    }
  };

  const isSubmitting = informant.isSubmitting || isCompleting;

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open && !isSubmitting) onClose?.(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 radix-dialog-overlay-motion" />
        <AlertDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] max-w-md bg-white rounded-lg shadow-xl z-50 p-4 md:p-6 max-h-[90vh] overflow-y-auto transition-all duration-300 radix-dialog-content-inset-motion">
          <VisuallyHidden.Root>
            <AlertDialog.Title>
              {currentStep === 'completion' ? 'Setup complete' : `Add a ${t.lowerSingular}`}
            </AlertDialog.Title>
          </VisuallyHidden.Root>

          {/* Close button — disabled during submission */}
          {currentStep === 'form' && (
            <button
              type="button"
              onClick={() => { if (!isSubmitting) onClose?.(); }}
              disabled={isSubmitting}
              className="absolute top-3 right-3 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
          )}

          {currentStep === 'form' ? (
            <>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Add a {t.lowerSingular}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Add someone who knows you well to answer questions about your life to support your assessment.
              </p>

              {showSkip ? (
                <SkipForm
                  skipReason={informant.skipReason}
                  onSkipReasonChange={(value) => setInformant((prev) => ({
                    ...prev,
                    skipReason: value,
                    hasError: false,
                    errorMessage: '',
                  }))}
                  onGoBack={() => setShowSkip(false)}
                  terminology={t}
                  hasError={informant.hasError}
                  errorMessage={informant.errorMessage}
                />
              ) : (
                <InformantDetailsForm
                  informant={informant}
                  onChange={(field, value) => setInformant((prev) => ({ ...prev, [field]: value, hasError: false, errorMessage: '' }))}
                  onToggleSkip={() => setShowSkip(true)}
                  terminology={t}
                />
              )}

              {submitError && <WarningPanel variant="error" className="mt-4">{submitError}</WarningPanel>}

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? <><ButtonSpinner /> Setting up...</> : (showSkip ? 'Skip and continue' : `Set up ${t.lowerSingular}`)}
                </button>
              </div>
            </>
          ) : (
            <CompletionScreen
              informant={informant}
              wasSkipped={wasSkipped}
              terminology={t}
              onFinish={() => onClose?.()}
            />
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
